import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { API } from 'constants';
import client from '../../../services/library-api';

import {
    startNewJob,
    getStartNewJobFormDataInitialState,
} from '../../../helpers/startNewJobFormUtils';

import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useHandlePageDataLoadError } from '../../../hooks/useHandlePageDataLoadError';

import { ArrowGoBackLineIcon } from '../../../design-system/Icons';
import Button from '../../../design-system/Button/Button';
import Loading from '../../../components/Loading';
import ErrorAlert from '../../../design-system/ErrorAlert/ErrorAlert';
import SuccessStartNewJobState from './SuccessStartNewJobState/SuccessStartNewJobState';
import StartNewJobFormBlock from './StartNewJobFormBlock/StartNewJobFormBlock';

const StartNewJobPage = () => {
    const { processId } = useParams();

    const navigate = useNavigate();
    const location = useLocation();
    const backLinkHref = location.state?.from ?? '/playbooks';

    const [process, setProcess] = useState(null);
    const [formData, setFormData] = useState(null);

    const [newJobId, setNewJobId] = useState(null);
    const [startNewJobState, setStartNewJobState] = useState('default'); // states are "default", "loading", "success"
    const [errorAlert, setErrorAlert] = useState(null);

    const { handlePageDataLoadError } = useHandlePageDataLoadError();

    useEffect(() => {
        const fetchProcessData = async () => {
            try {
                const { data } = await client.get(
                    `${API.ROUTES.library.process}${processId}/?shared=True`
                );
                setProcess(data);
                setFormData(() =>
                    getStartNewJobFormDataInitialState({
                        inputs: data.inputs,
                        allowDocsMapping: true,
                        replaceOnlyFilledTags: false,
                    })
                );
            } catch (e) {
                handlePageDataLoadError({
                    e,
                    redirectPath: '/dashboard',
                    generalErrorHandler: () => navigate(backLinkHref),
                });
            }
        };

        resetProcessState();
        fetchProcessData();
    }, [processId]);

    useDocumentTitle('Start New Job');

    const resetProcessState = () => {
        setProcess(null);
        setFormData(null);
        setNewJobId(null);
        setStartNewJobState('default');
    };

    const handleStartNewJob = async () => {
        await startNewJob({
            formData,
            setFormData,
            processId,
            setNewJobId,
            setStartNewJobState,
            setErrorAlert,
        });
    };

    const onStartFromSameProcess = () => {
        // reset form data
        setFormData(() =>
            getStartNewJobFormDataInitialState({
                inputs: process.inputs,
                allowDocsMapping: true,
                replaceOnlyFilledTags: false,
            })
        );
        setStartNewJobState('default');
        setNewJobId(null);
    };

    return (
        <div className="page-position flex flex-col bg-neutral-50 overflow-y-auto hide-scrollbar">
            {process && (
                <>
                    <div className="py-3 px-4 sm:px-6 bg-white border-b-1 border-neutral-200 flex flex-col items-start min-[380px]:flex-row min-[380px]:items-center gap-x-5 gap-y-0.5">
                        <Button
                            type="link"
                            size="xs"
                            text="Back"
                            leadingIcon={ArrowGoBackLineIcon}
                            onClick={() => navigate(backLinkHref)}
                        />
                        <p className="font-body text-body-regular-s text-neutral-300">
                            Agents / Agent Overview
                        </p>
                    </div>
                    {startNewJobState !== 'success' && (
                        <StartNewJobFormBlock
                            process={process}
                            formData={formData}
                            setFormData={setFormData}
                            startNewJobState={startNewJobState}
                            handleStartNewJob={handleStartNewJob}
                        />
                    )}

                    {startNewJobState === 'success' && (
                        <SuccessStartNewJobState
                            jobId={newJobId}
                            processName={process?.name}
                            viewMode="fullScreen"
                            onStartFromSameProcess={onStartFromSameProcess}
                        />
                    )}
                </>
            )}

            {!process && (
                <div className="w-full h-full flex items-center justify-center">
                    <Loading />
                </div>
            )}

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </div>
    );
};

export default StartNewJobPage;
